// Learn (F1; mockups docs/renders/learn/l01–l03, provisional until the owner picks): short,
// free sessions on three tracks, built from the same cards, machines and spaced repetition (SRS)
// as the Arcade. Answers count to the child's daily goal (domain/arcade/plays), which earns a
// play to spend in the Arcade. Pure.
// WI-10: the tracks come from the trip's language kit, so any destination brings its own:
//   · everyday: the phrases you say (Speak Up's cards);
//   · script:<deck>: one track per script the kit lists, one character at a time (katakana,
//     then hiragana for Japanese; hangul for Korean). A later script opens once the first
//     script's basic characters have all been met;
//   · together: real words and signs made of them, once 20 characters have been met;
//   · numbers: numbers, prices and times (Number Rush's cards), taught here, tested in the Arcade.
// One progress model everywhere: a card is *met* once answered (box > 0) and *learned* at
// box ≥ LEARNED_BOX; a track counts both over its own cards.
import type { LearnTrack } from '../../shared/controls'
import type { Pack } from '../../shared/pack/schema'
import { t } from '../strings/en'
import { basicIds, basicKanaIds, scriptDecks } from './kana'
import { cardsOf } from './arcade/cards'
import { kanaCapsules } from './arcade/cardSets/kanaCapsules'
import { numberRush } from './arcade/cardSets/numberRush'
import { speakUp } from './arcade/cardSets/speakUp'
import { buildSession } from './arcade/session'
import { isLearned } from './arcade/srs'
import type { Item, CardSet, CardSetCtx } from './arcade/types'

/** "Putting it together" opens once this many script characters have been met. */
export const TOGETHER_AFTER_SEEN = 20
export const LEARN_SESSION = 5

export type TrackKind = 'everyday' | 'script' | 'together' | 'numbers'

/** A track this trip has: what the Learn home lists and the parent's controls switch. */
export interface Track {
  id: LearnTrack
  kind: TrackKind
  /** A script track's deck. */
  deck?: string
  /** Which script it is, 0 = the first (the others wait for it). */
  index?: number
}

const scriptId = (deck: string): LearnTrack => `script:${deck}`
const kindOf = (track: LearnTrack): TrackKind => (track.startsWith('script') ? 'script' : (track as TrackKind))

/** The real-word pool: the reading capsule's cards that aren't single characters. */
const togetherPool = (pack: Pack) => {
  const chars = new Set(scriptDecks(pack).flatMap((d) => cardsOf(pack, d).map((c) => c.id)))
  return kanaCapsules.pool(pack).filter((id) => !chars.has(id))
}

/** The trip's tracks, in the order the Learn home lists them; a track with no cards isn't one. */
export function tracksFor(pack: Pack): Track[] {
  const scripts = scriptDecks(pack)
  return [
    ...(speakUp.pool(pack).length ? [{ id: 'everyday' as const, kind: 'everyday' as const }] : []),
    ...scripts.map((deck, index) => ({ id: scriptId(deck), kind: 'script' as const, deck, index })),
    ...(scripts.length && togetherPool(pack).length ? [{ id: 'together' as const, kind: 'together' as const }] : []),
    ...(numberRush.pool(pack).length ? [{ id: 'numbers' as const, kind: 'numbers' as const }] : []),
  ]
}

/**
 * The tracks switched on in a child's controls. `script` (stored before each script was its own
 * track) switches on every script track; ids this trip doesn't have (another kit's script) are
 * left out.
 */
export function enabledTracks(pack: Pack, selected: readonly LearnTrack[]): Track[] {
  return tracksFor(pack).filter((tr) => selected.includes(tr.id) || (tr.kind === 'script' && selected.includes('script')))
}

/**
 * A controls list with `script` spelled out as this trip's script tracks, so one of them can be
 * switched off on its own. Other ids (another trip's scripts) are kept as they are.
 */
export function explicitTracks(pack: Pack, selected: readonly LearnTrack[]): LearnTrack[] {
  if (!selected.includes('script')) return [...selected]
  const scripts = scriptDecks(pack).map(scriptId)
  return [...new Set([...selected.filter((x) => x !== 'script'), ...scripts])]
}

/** The deck a script track teaches (`script` alone: the first script). */
function deckOf(track: LearnTrack, pack: Pack): string {
  return track === 'script' ? (scriptDecks(pack)[0] ?? '') : track.slice('script:'.length)
}

/** Each track as a machine: an existing machine's cards and items with the track's own pool. */
export function trackCardSet(track: LearnTrack): CardSet {
  const base = { sessionSize: LEARN_SESSION, newPerSession: 2, blurb: t.learn.blurbs[kindOf(track)] ?? '' }
  if (track === 'everyday') return { ...speakUp, ...base }
  if (track === 'numbers') return { ...numberRush, ...base }
  if (track === 'together') return { ...kanaCapsules, ...base, pool: togetherPool }
  return {
    ...kanaCapsules,
    ...base,
    pool: (pack) => cardsOf(pack, deckOf(track, pack)).map((c) => c.id),
    eligible: () => true,
  }
}

/** The first script's basic characters met so far (what opens "together" and the later scripts). */
export function seenScript(ctx: Pick<CardSetCtx, 'pack' | 'progress'>): number {
  return basicKanaIds(ctx.pack).filter((id) => (ctx.progress.cards[id]?.box ?? 0) > 0).length
}

export interface TrackInfo {
  track: Track
  /** Cards answered at least once (box > 0). */
  met: number
  /** Cards at box ≥ LEARNED_BOX. */
  learned: number
  /** The track's own cards: the one denominator for both. */
  total: number
  open: boolean
  /** What a closed track waits for: characters of the first script to meet. */
  opensAt?: number
}

export function trackInfo(track: Track, ctx: CardSetCtx): TrackInfo {
  const pool = trackCardSet(track.id).pool(ctx.pack)
  const cards = pool.map((id) => ctx.progress.cards[id])
  const gate = track.kind === 'together' ? TOGETHER_AFTER_SEEN : track.kind === 'script' && track.index ? basicIds(ctx.pack, scriptDecks(ctx.pack)[0]).length : 0
  const open = !gate || seenScript(ctx) >= gate
  return {
    track,
    met: cards.filter((c) => (c?.box ?? 0) > 0).length,
    learned: cards.filter((c) => isLearned(c)).length,
    total: pool.length,
    open,
    ...(open ? {} : { opensAt: gate }),
  }
}

/** All the trip's tracks together, each card once: "N met · M learned" for a grown-up's summary. */
export function learnTotals(pack: Pack, progress: CardSetCtx['progress']): { met: number; learned: number } {
  const ids = new Set(tracksFor(pack).flatMap((tr) => trackCardSet(tr.id).pool(pack)))
  const cards = [...ids].map((id) => progress.cards[id])
  return { met: cards.filter((c) => (c?.box ?? 0) > 0).length, learned: cards.filter((c) => isLearned(c)).length }
}

/** A script track's deck title ("Katakana"), from the pack; the first script's for `script`. */
export function scriptTitle(pack: Pack, track: LearnTrack = 'script'): string | undefined {
  const deck = deckOf(track, pack)
  return pack.decks.find((d) => d.id === deck)?.title
}

/** A short glyph for a track's badge, from its own first card (すみ, ア, あ, アイス). */
export function trackGlyph(pack: Pack, track: Track): string {
  if (track.kind === 'numbers') return '123'
  const first = trackCardSet(track.id).pool(pack)[0]
  const card = first ? pack.decks.flatMap((d) => d.cards).find((c) => c.id === first) : undefined
  const text = !card ? '' : card.kind === 'reading' ? card.script : card.kind === 'speak' ? card.target : (card.say ?? '')
  return [...text].slice(0, track.kind === 'together' ? 3 : 2).join('')
}

/**
 * A Learn session's cards. `n` varies the pick within the day (sessions so far), like a play
 * number. Gentle difficulty drops one wrong option, so choices are 3 instead of 4.
 */
export function learnSession(track: LearnTrack, ctx: CardSetCtx, n: number, difficulty: 'gentle' | 'standard' | 'stretch'): Item[] {
  const items = buildSession(trackCardSet(track), ctx, 100 + n)
  return difficulty === 'gentle' ? items.map(gentle) : difficulty === 'stretch' ? stretch(items) : items
}

/** Stretch has one more answer to choose from than Standard. */
export const STRETCH_OPTIONS = 5

/** How an answer is written: kana or kanji, Latin letters, or a number/price/time. */
const scriptOf = (s: string) => (/[぀-ヿ一-鿿가-힣]/.test(s) ? 'jp' : /^[\d\s¥$€£:.,/~-]+$/.test(s) ? 'num' : 'latin')

/**
 * The kind of question an item is, for borrowing answers between items: its deck (the card id's
 * first part: kana, num, shelf, survival…), how it's asked (a big glyph, a sentence, a sound, a
 * glyph to pick) and how its right answer is written. Not its question text: mcq cards have none,
 * and Speak Up puts the phrase's romaji in it.
 */
const choiceKind = (i: Extract<Item, { type: 'choice' }>) =>
  [i.cardId.split('-')[0], i.big ? 'big' : '', i.text ? 'text' : '', i.listen ? 'listen' : '', i.glyphs ? 'glyphs' : '', scriptOf(i.options[i.answer] ?? '')].join('|')

/**
 * Stretch difficulty (WI-07): each question gets a fifth answer to choose from, a wrong one taken
 * from the session's other questions of the same kind (choiceKind), so it's as plausible as the rest.
 * Placed at a spot that follows from the card, so a session stays the same when it's rebuilt. A
 * question with nothing of its kind to borrow from stays as it is.
 */
export function stretch(items: Item[]): Item[] {
  return items.map((item) => {
    if (item.type !== 'choice' || item.options.length >= STRETCH_OPTIONS) return item
    const right = item.options[item.answer]!
    const kind = choiceKind(item)
    const extra = items
      .flatMap((o) => (o !== item && o.type === 'choice' && choiceKind(o) === kind ? o.options : []))
      .find((x) => x !== right && !item.options.includes(x) && scriptOf(x) === scriptOf(right))
    if (extra === undefined) return item
    const at = [...item.cardId].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % (item.options.length + 1)
    const options = [...item.options.slice(0, at), extra, ...item.options.slice(at)]
    return { ...item, options, answer: options.indexOf(right) }
  })
}

export function gentle(item: Item): Item {
  if (item.type !== 'choice' || item.options.length <= 3) return item
  const drop = item.options.findIndex((_, i) => i !== item.answer)
  const options = item.options.filter((_, i) => i !== drop)
  return { ...item, options, answer: options.indexOf(item.options[item.answer]!) }
}

export interface IslandGlyphs {
  /** On the chalkboard: the next script character to learn. */
  board: string
  /** On the easel card: the other script's first character (hiragana beside katakana). */
  card: string
  /** Toy blocks in the snow: characters already seen (the deck's first ones before any). */
  blocks: string[]
}

/** The characters the Learn island shows, from the pack's script decks. Empty strings when there are none. */
export function islandGlyphs(pack: Pack, progress: Pick<CardSetCtx['progress'], 'cards'>): IslandGlyphs {
  const chars = (deck: string) => cardsOf(pack, deck).flatMap((c) => (c.kind === 'reading' && [...c.script].length === 1 ? [{ id: c.id, ch: c.script }] : []))
  const [first = [], second = []] = scriptDecks(pack).map(chars)
  const seen = first.filter((c) => (progress.cards[c.id]?.box ?? 0) > 0)
  const next = first.find((c) => (progress.cards[c.id]?.box ?? 0) === 0) ?? first[0]
  const blocks = (seen.length ? seen.slice(-3) : first.slice(1, 4)).map((c) => c.ch)
  return { board: next?.ch ?? '', card: second[0]?.ch ?? first[1]?.ch ?? '', blocks }
}

export interface SummaryCard {
  cardId: string
  /** What the child saw: the character, word or phrase. */
  label: string
  /** Never missed this session (a wrong answer or a skip makes it one to try again). */
  ok: boolean
  /** Met for the first time this session. */
  met: boolean
}

export interface SessionSummary {
  /** Each card once, in the order first asked (a re-ask is the same card, not another one). */
  cards: SummaryCard[]
  met: SummaryCard[]
  again: SummaryCard[]
}

const labelOf = (item: Item): string => item.reveal.big ?? item.big ?? item.reveal.line

/**
 * The end of a Learn session (WI-11): its cards once each, the ones met for the first time and
 * the ones to try again. `results` is per position, as the session recorded them; a card asked
 * again after a miss stays "try again" even when the re-ask was right (its box went back down).
 */
export function sessionSummary(items: readonly Item[], results: readonly ('right' | 'wrong' | undefined)[]): SessionSummary {
  const byId = new Map<string, SummaryCard>()
  items.forEach((item, i) => {
    const result = results[i]
    const card = byId.get(item.cardId) ?? { cardId: item.cardId, label: labelOf(item), ok: true, met: false }
    if (item.isNew) card.met = true
    if (result === 'wrong') card.ok = false
    // Only cards actually reached (closing early leaves the rest unanswered).
    if (result !== undefined || byId.has(item.cardId)) byId.set(item.cardId, card)
  })
  const cards = [...byId.values()]
  return { cards, met: cards.filter((c) => c.met), again: cards.filter((c) => !c.ok) }
}
